import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Business,
  People,
  Message,
  Settings,
  TrendingUp,
  Refresh,
  Add,
  Edit,
  CardMembership,
  Build,
  Subscriptions,
} from '@mui/icons-material';
import apiService from '../../services/api';
import authService from '../../services/auth';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState({
    totalTenants: 0,
    activeTenants: 0,
    totalUsers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      console.log('SuperAdminDashboard: Fetching stats...');
      console.log('SuperAdminDashboard: Current user:', authService.getUser());
      console.log('SuperAdminDashboard: Session token:', authService.getSessionToken());
      
      const response = await apiService.get('/superadmin/dashboard/stats');
      console.log('SuperAdminDashboard: API response:', response);
      
      if (response.success) {
        setStats(response.data);
      } else {
        setError(response.message || 'Failed to fetch dashboard statistics');
      }
    } catch (err) {
      console.error('SuperAdminDashboard: Error details:', err);
      setError('Error fetching dashboard data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, description }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Box
            sx={{
              backgroundColor: `${color}.light`,
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" component="div" fontWeight="bold" color={color + '.main'}>
          {value}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold" color="primary.main">
          Super Admin Dashboard
        </Typography>
        <Tooltip title="Refresh Statistics">
          <IconButton onClick={fetchDashboardStats} color="primary">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Tenants"
            value={stats.totalTenants}
            icon={<Business />}
            color="primary"
            description="All registered tenants"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Tenants"
            value={stats.activeTenants}
            icon={<TrendingUp />}
            color="success"
            description="Currently active tenants"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={<People />}
            color="info"
            description="All system users"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Quick Actions
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="contained"
                startIcon={<Add />}
                fullWidth
                href="/superadmin/tenants/new"
              >
                Add New Tenant
              </Button>
              <Button
                variant="outlined"
                startIcon={<People />}
                fullWidth
                href="/superadmin/tenants"
              >
                View All Tenants
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              System Status
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Database Connection</Typography>
                <Chip label="Online" color="success" size="small" />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Email Service</Typography>
                <Chip label="Active" color="success" size="small" />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">API Status</Typography>
                <Chip label="Healthy" color="success" size="small" />
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Subscription Management Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" fontWeight="bold" mb={3} color="primary.main">
          📋 Subscription Management
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', cursor: 'pointer' }} 
                   onClick={() => window.location.href = '/superadmin/subscription-plans'}>
              <CardMembership sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Subscription Plans
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage plans, pricing, and features
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', cursor: 'pointer' }}
                   onClick={() => window.location.href = '/superadmin/subscription-features'}>
              <Build sx={{ fontSize: 40, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Features
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage subscription features
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', cursor: 'pointer' }}
                   onClick={() => window.location.href = '/superadmin/tenant-subscriptions'}>
              <Subscriptions sx={{ fontSize: 40, color: 'info.main', mb: 2 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Tenant Subscriptions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage subscriptions
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', cursor: 'pointer' }}
                   onClick={() => window.location.href = '/superadmin/subscription-analytics'}>
              <TrendingUp sx={{ fontSize: 40, color: 'warning.main', mb: 2 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Subscription insights and stats
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SuperAdminDashboard; 